/** @jsx jsx */
/** @jsxFrag null */
// @ts-strict-ignore
import debounce from 'lodash/debounce';
import {jsx, ParamComponent} from 'panel';

import {NoExtension, Section, SectionItem} from 'common/components/items-menu/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {reporter} from 'common/report/error-reporting';
import {getProjectId} from 'common/report/util';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getBrowseUrl,
  getDisplayContext,
  getSearchContexts,
  getSearchIconFromDisplayContext,
  isBrowseUrl,
  searchUtils,
} from 'common/report/util/search';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  getTrackingPropertiesForResult,
  getTrackingPropertiesForResultsLoad,
  SearchSource,
  SearchTrackedProperty,
  TypeaheadTrackedEvent,
} from 'common/report/util/search/tracking';
import {
  RecentSearchItemType,
  SearchDisplayContextTranslation,
  SearchQueryFilters,
  SearchQuerySort,
  SearchResult,
  SearchSortKey,
  SearchSortOrder,
} from 'common/report/util/search/types';
import {colors} from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {truncate} from 'common/util/string';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {UserType} from 'common/widgets/user-list/constants';
import {UserListEl} from 'common/widgets/user-list';
import {IconSizeType} from 'common/constants';
import {ResultDisplayMode, SearchInputEl} from 'common/widgets/search-input';
import {InputTheme} from 'common/components/input/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {UserIconEl} from 'common/widgets/user-icon';
import {GlobalSearchController} from 'common/report/model-resources/global-search-controller';
import {Translatable} from 'common/i18n';
import {panel2react} from 'common/util/react';

const MAX_CREATORS_SHOWN = 2;
const MAX_RESULTS_SHOWN = 3;
const CREATOR_SEARCH_MIN_LENGTH = 3;

// this determines what section to show
// sometimes we want to only show entity
export enum GlobalSearchSectionType {
  Creator = `creator`,
  Entity = `entity`,
  BrowseAll = `browse-all`,
  BoardTemplate = `board-template`,
}

type GlobalSearchParams = {
  // prevent link so we do not redirect to the entity
  preventLink?: boolean;
  // do not show the recently searched section
  skipRecentlySearched?: boolean;
  showingSections: GlobalSearchSectionType[];
  onCompleteSearch?: () => void;
  // proxy the search-input-el event so parent knows what entity has been clicked
  onItemClick?: (item: SearchSectionItem) => void;
};

export enum GlobalSearchItemType {
  Creator = `creator`,
  Recent = `recent`,
  Entity = `entity`,
  BrowseAll = `browse-all`,
  ReadOnly = `read-only`,
}

type GlobalSearchJsxProps = {
  params: GlobalSearchParams;
};

type CreatorSearchSectionItem = SectionItem<TopCreator, {type: GlobalSearchItemType.Creator}>;
type EntitySearchSectionItem = SectionItem<SearchResult, {type: GlobalSearchItemType.Entity}>;
type BrowseAllSearchSectionItem = SectionItem<string, {type: GlobalSearchItemType.BrowseAll}>;
type ReadOnlySearchSectionItem = SectionItem<string, {type: GlobalSearchItemType.ReadOnly}>;

type SearchSectionItem =
  | EntitySearchSectionItem
  | BrowseAllSearchSectionItem
  | ReadOnlySearchSectionItem
  | CreatorSearchSectionItem;

type SearchSection = Section<any, NoExtension, SearchSectionItem>;

interface GlobalSearchState {
  allowRecentItems?: boolean;
  errored?: boolean;
  open: boolean;
  loading?: boolean;
  searchedText?: string;
  searchResults: SearchResult[];
  searchInputEl?: HTMLElement;
}

enum SearchReason {
  ComponentInitialized = `component-initialized`,
  MenuOpened = `menu-opened`,
  SearchTextChanged = `search-text-changed`,
}

function userListRenderer(item: EntitySearchSectionItem) {
  const users: MixpanelUserItem[] = [
    {
      name: item.value.user,
      id: item.value.user_id,
      email: item.value.email,
      type: UserType.Mixpanel,
    },
  ];

  return (
    <UserListEl
      attrs={{
        users,
        'max-icons-displayed': 1,
        'user-count-override': item.value.unique_count,
        size: IconSizeType.Small,
      }}
    />
  );
}

function leftIconRenderer({
  icon,
  tooltipText,
  isErrored,
}: {
  icon: string;
  tooltipText?: Translatable;
  isErrored?: boolean;
}) {
  let color = null;
  if (isErrored) {
    color = colors.red1;
  }

  const iconComponent = <SvgIconEl attrs={{icon, color}} style={{margin: `8px`}} />;
  if (tooltipText) {
    return (
      <div style={{display: `flex`}}>
        {iconComponent}
        <TooltipEl attrs={{placement: Placement.Top}}>{tooltipText}</TooltipEl>
      </div>
    );
  }
  return iconComponent;
}

export class GlobalSearch extends ParamComponent<GlobalSearchParams, GlobalSearchState, IronContextRegistry> {
  get config() {
    return {
      template: () => {
        return (
          <SearchInputEl
            attrs={{elref: `searchInputCmp`}}
            params={{
              skipRecentlySearched: this.params.skipRecentlySearched,
              onEnterPressed: () => {
                if (!this.params.preventLink) {
                  this.track(TypeaheadTrackedEvent.ShowAllSelected, {'Pressed Enter': true});
                  this.redirectToBrowsePage();
                }
              },
              onItemClick: this.helpers.handleSearchSelection,
              onMenuOpen: () => {
                this.track(TypeaheadTrackedEvent.OpenTypeahead);
                this.handleSearchedTextUpdate(this.state.searchedText, {searchReason: SearchReason.MenuOpened});
              },
              onSearchedTextUpdate: (searchedText, isSuggestedItem) => {
                if (isSuggestedItem) {
                  this.track(TypeaheadTrackedEvent.RecentQuerySelected);
                }
                this.handleSearchedTextUpdate(searchedText, {searchReason: SearchReason.SearchTextChanged});
              },

              includeHrefRecentTerms: !this.params.preventLink,
              onTrackEvent: (event, props) => this.track(event, props ?? {}),
              searchedText: this.state.searchedText,
              sections: this.getSearchSections(),
              onCompleteSearch: this.params.onCompleteSearch,
              resultDisplayMode: ResultDisplayMode.List,
              theme: InputTheme.Default,
            }}
          />
        );
      },
      contexts: [
        `currentProject` as const,
        `projectTopCreators` as const,
        `urlManager` as const,
        `globalSearchController` as const,
        `i18n` as const,
      ],
      defaultState: {
        allowRecentItems: true,
        errored: false,
        loading: false,
        searchResults: [],
        searchedText: DEFAULT_SEARCH_QUERY_STRING,
        open: false,
      },
      params: {
        showingSections: Array,
        onCompleteSearch: Function,
        preventLink: Boolean,
        skipRecentlySearched: Boolean,
        onItemClick: Function,
      },
      defaultParams: {
        showingSections: [],
        preventLink: false,
      },
      helpers: {
        handleSearchSelection: (item: SearchSectionItem) => {
          if (item.type === GlobalSearchItemType.Entity) {
            if (this.params.onItemClick && this.params.preventLink) {
              this.params.onItemClick(item);
            } else {
              this.update({searchResults: [], searchedText: ``});
            }
            this.track(TypeaheadTrackedEvent.ResultSelected, getTrackingPropertiesForResult(item.value));
          } else if (item.type === GlobalSearchItemType.BrowseAll) {
            this.track(TypeaheadTrackedEvent.ShowAllSelected);
          } else if (item.type === GlobalSearchItemType.Creator) {
            if (this.params.onItemClick && this.params.preventLink) {
              this.params.onItemClick(item);
            } else {
              this.update({searchResults: [], searchedText: ``});
            }
            const recentItem = {
              id: item.value.id,
              userEmail: item.value.email,
              key: `user-${item.value.id}`,
              label: item.value.name,
              type: RecentSearchItemType.User,
            };
            searchUtils.addRecentItem(recentItem);
            this.track(TypeaheadTrackedEvent.CreatorFilterSelected);
          }
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setDefaultSearchText();
    this.handleSearchedTextUpdate(this.state.searchedText, {searchReason: SearchReason.ComponentInitialized});
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  resourceCustomSubscriptions = new Map([
    [
      GlobalSearchController,
      () => {
        this.setDefaultSearchText();
      },
    ],
  ]);

  getSearchParamsContext() {
    return this.getContext(`globalSearchController`);
  }

  setDefaultSearchText() {
    const persistedSearchedText = this.getSearchParamsContext().getSearchedText();
    if (isBrowseUrl()) {
      if (persistedSearchedText && persistedSearchedText !== this.state.searchedText) {
        this.update({searchedText: persistedSearchedText});
      }
    } else {
      this.update({searchedText: ``});
    }
  }

  handleSearchedTextUpdate(searchedText: string, {searchReason}: {searchReason: SearchReason}): void {
    if (this.state.searchedText !== searchedText || !this.state.searchResults.length) {
      this.update({searchedText, loading: true});
      this.executeSearch(searchedText, {searchReason});
    }
  }

  redirectToBrowsePage() {
    this.getUrlManager().setLocation(getBrowseUrl({queryParam: {q: this.state.searchedText || ``}}));
  }

  private getUrlManager() {
    return this.getContext(`urlManager`);
  }

  getSearchSections(): SearchSection[] {
    const shouldLinkSearchedText = this.state.searchedText !== DEFAULT_SEARCH_QUERY_STRING;

    return [
      ...(this.params.showingSections.includes(GlobalSearchSectionType.Creator) ? this.getSearchCreatorSection() : []),
      ...this.getSearchResultSections(),
      ...(this.params.showingSections.includes(GlobalSearchSectionType.BoardTemplate)
        ? this.getTemplateSearchResultSections()
        : []),
      ...(shouldLinkSearchedText && this.params.showingSections.includes(GlobalSearchSectionType.BrowseAll)
        ? [this.getBrowseItem()]
        : []),
    ];
  }

  private getBrowseItem(): SearchSection {
    const href = getBrowseUrl({queryParam: {q: this.state.searchedText || ``}});
    return {
      isLoading: false,
      iconRenderer: () => leftIconRenderer({icon: `arrow-diagonal`}),
      items: [
        {
          label: this.t(`search.show-all-results`),
          type: GlobalSearchItemType.BrowseAll,
          href,
        },
      ],
    };
  }

  private getSearchSortKey(searchedText: string): SearchSortKey | null {
    if (searchedText === DEFAULT_SEARCH_QUERY_STRING) {
      if (this.getContext(`projectTopCreators`).hasViewedContent()) {
        return SearchSortKey.RecentlyViewed;
      }
      return SearchSortKey.Popularity;
    }
    return null;
  }

  private executeSearch = debounce(async (searchedText, {searchReason}: {searchReason: SearchReason}) => {
    this.update({searchResults: [], loading: true});

    const queryFilters: SearchQueryFilters = {
      email: [],
      type: getSearchContexts().map((value) => ({value, negated: false})),
    };

    const trackingProps = {
      [SearchTrackedProperty.OwnerFilters]: [],
      [SearchTrackedProperty.ReportFilters]: [],
      [SearchTrackedProperty.SearchQuery]: searchedText,
      [SearchTrackedProperty.SearchReason]: searchReason,
    };
    try {
      const sortKey = this.getSearchSortKey(searchedText);
      const querySort: SearchQuerySort = sortKey ? {key: sortKey, order: SearchSortOrder.Descending} : null;

      const searchResults = await searchUtils.executeSearch({
        queryFilters,
        querySort,
        searchedText,
        sharedOnly: true,
        limit: 100,
      });
      if (searchedText === this.state.searchedText && searchResults) {
        this.update({loading: false, searchResults});

        this.track(TypeaheadTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          ...getTrackingPropertiesForResultsLoad(searchResults),
        });
      }
    } catch (err) {
      reporter.error(err);
      this.update({errored: true, loading: false});
    }
  }, 200);

  private getTemplateSearchResultSections(): SearchSection[] {
    const {loading, searchedText} = this.state;
    const items = [...searchUtils.getTemplateItems(searchedText, this.getContext(`currentProject`).get().value)];
    return items.length === 0 || loading
      ? []
      : this.processSearchResultItems({
          items,
          label: this.t(`search.templates`),
          loading,
          maxSearchResults: items.length,
        });
  }

  private getSearchResultSections(): SearchSection[] {
    const {loading, searchResults, searchedText} = this.state;
    const items = searchResults.map((item: SearchResult): EntitySearchSectionItem => {
      return {
        ...(this.params.preventLink ? {} : {href: item.link}),
        icon: getSearchIconFromDisplayContext(getDisplayContext(item)),
        label: truncate(item.name, 50),
        subLabel: item.dashboard,
        type: GlobalSearchItemType.Entity,
        value: item,
      };
    });
    const label =
      {
        [SearchSortKey.Popularity]: this.t(`search.most-viewed`),
        [SearchSortKey.RecentlyViewed]: this.t(`search.recently-viewed`),
      }[this.getSearchSortKey(searchedText)] ?? this.t(`search.boards-and-reports`);
    return this.processSearchResultItems({items, label, loading, maxSearchResults: MAX_RESULTS_SHOWN});
  }

  private processSearchResultItems({items, label, loading, maxSearchResults}): SearchSection[] {
    const {errored} = this.state;

    const results: SearchSection = {
      label: loading ? null : label,
      isLoading: loading,
      hideTopDivider: true,
      actionsRenderer: (item: EntitySearchSectionItem) =>
        !errored && items.length > 0 ? userListRenderer(item) : null,
      iconRenderer: (item: SearchSectionItem) => {
        if (item.type === GlobalSearchItemType.Entity) {
          return leftIconRenderer({
            icon: item.icon,
            tooltipText: SearchDisplayContextTranslation[item.value.report],
            isErrored: errored,
          });
        }
        return null;
      },
      items:
        items.length > 0
          ? items.slice(0, maxSearchResults)
          : [
              {
                icon: errored ? `warning` : `search`,
                isReadOnly: true,
                label: errored ? this.t(`search.error`) : this.t(`search.no-items-matched`),
                subLabel: errored ? this.t(`search.try-again`) : null,
                type: GlobalSearchItemType.ReadOnly,
              },
            ],
    };

    return [results];
  }

  private getSearchCreatorSection(): SearchSection[] {
    const creators = this.getContext(`projectTopCreators`).getCreators();
    if (this.state.searchedText?.length < CREATOR_SEARCH_MIN_LENGTH || !creators) {
      return [];
    }

    const rank = (creator: TopCreator) => fuzzyMatch(creator.name, this.state.searchedText)?.score ?? 0;

    const rankedCreators = creators
      .filter((creator) => rank(creator) > 0)
      .sort((creator1, creator2) => rank(creator2) - rank(creator1))
      .slice(0, MAX_CREATORS_SHOWN);

    const items: CreatorSearchSectionItem[] = rankedCreators.map((creator: TopCreator) => {
      return {
        label: creator.name,
        type: GlobalSearchItemType.Creator,
        value: creator,
        href: getBrowseUrl({queryParam: {email: creator.email}}),
      };
    });

    return [
      {
        label: `Creators`,
        items,
        iconRenderer: (item: CreatorSearchSectionItem) => {
          return (
            <UserIconEl
              attrs={{
                id: item.value.id,
                email: item.value.email,
                name: item.value.name,
                size: IconSizeType.Small,
              }}
            />
          );
        },
      },
    ];
  }

  private track(eventName: string, props: Obj = {}) {
    mixpanel.track(eventName, {
      ...this.getCommonTrackingProperties(),
      ...props,
      project_id: getProjectId(),
    });
  }

  private getCommonTrackingProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.Source]: SearchSource.Dropdown,
      [SearchTrackedProperty.ContentDiscoveryType]: this.state.searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.Browse,
    };
  }
}

export const GlobalSearchEl = defineAndMakeJsxFactory<GlobalSearchJsxProps>(`mp-global-search`, GlobalSearch);
export const GlobalSearchReact = panel2react<GlobalSearchJsxProps>(`mp-global-search`, GlobalSearch);
